import { CreatableSelect } from '@dealstack/frontend/shared/components/forms/CreatableSelect/CreatableSelect';
import { Button, Flex, LoadingOverlay, Select, Text, TextInput } from '@mantine/core';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { DataLoanDefinitionTct } from '@dealstack/shared/lib/generated';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon.tsx';
import {
	FormField,
	StyledDrawer,
} from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/MasterDocuments/styles.ts';
import { useManageLoanDrawer } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/ProgrammeSettings/Loans/components/ManageLoanDrawer/useManageLoanDrawer.ts';
import { useParams } from 'react-router-dom';

interface LoanFormData {
	id?: string;
	type: string;
	name: string;
	funderId: string;
	funderIsNew?: boolean;
	funderName?: string;
}

interface Props {
	opened: boolean;
	onClose: () => void;
	loanData: DataLoanDefinitionTct | null;
	onSave: (loan: LoanFormData) => void;
	onRemove: (loanId: string) => void;
	isLoading?: boolean;
}

export const ManageLoanDrawer = ({
	opened,
	onClose,
	loanData,
	onSave,
	onRemove,
	isLoading = false,
}: Props) => {
	const { dealId } = useParams();
	const tMipManage = useTranslationMipManage();

	const {
		type,
		setType,
		name,
		setName,
		funderId,
		onFunderChange,
		handleSave,
		handleRemove,
		isEditMode,
		isFormValid,
		funderOptions,
		onCreateNewFunder,
		isLoadingFunders,
		loanTypeOptions,
	} = useManageLoanDrawer({
		dealId: dealId || '',
		opened,
		loanData,
		onSave,
		onRemove,
		onClose,
	});

	const getDrawerTitle = () => {
		if (isEditMode) {
			return tMipManage('root.loans.drawer.title.edit');
		} else {
			return tMipManage('root.loans.drawer.title.add');
		}
	};

	const getSubmitButtonText = () => {
		if (isEditMode) {
			return tMipManage('root.loans.drawer.button.update');
		} else {
			return tMipManage('root.loans.drawer.button.add');
		}
	};

	return (
		<StyledDrawer
			opened={opened}
			onClose={onClose}
			position="right"
			size="md"
			transitionProps={drawerTransitionProps}
			title={getDrawerTitle()}
		>
			<Flex direction="column" h="100%" mih={400}>
				<LoadingOverlay
					visible={isLoading || isLoadingFunders}
					overlayProps={{ blur: 2 }}
				/>

				<div style={{ flex: 1 }}>
					<FormField>
						<Select
							label={tMipManage('root.loans.drawer.label.type')}
							placeholder={tMipManage('root.loans.drawer.placeholder.type')}
							size="md"
							required
							value={type}
							onChange={(value) => setType(value || '')}
							data={loanTypeOptions}
							rightSection={<Icon variant="caret-down" size="lg" />}
							disabled={isLoading}
						/>
					</FormField>

					<FormField>
						<TextInput
							label={tMipManage('root.loans.drawer.label.name')}
							placeholder={tMipManage('root.loans.drawer.placeholder.name')}
							size="md"
							required
							value={name}
							onChange={(e) => setName(e.target.value)}
							disabled={isLoading}
						/>
					</FormField>

					<FormField>
						<CreatableSelect
							label={tMipManage('root.loans.drawer.label.lender')}
							placeholder={tMipManage('root.loans.drawer.placeholder.lender')}
							size="md"
							required
							value={funderId}
							onChange={onFunderChange}
							data={funderOptions}
							shouldCreate={(query, data) => {
								if (!query) {
									return false;
								}
								const funder = data.find((funder) => funder.label === query);
								return !funder;
							}}
							onCreate={onCreateNewFunder}
							getCreateLabel={(query) => (
								<Flex align="center" gap="0.5rem">
									<Icon variant="add" />
									<Text>{tMipManage('root.loans.drawer.create_new_lender')}</Text>
									<Text>"{query}"</Text>
								</Flex>
							)}
							disabled={isLoading || isLoadingFunders}
						/>
					</FormField>
				</div>

				<Flex direction="column" align="flex-start" gap="1rem" mt="auto">
					<Button
						fullWidth
						onClick={handleSave}
						disabled={!isFormValid || isLoading}
						loading={isLoading}
					>
						{getSubmitButtonText()}
					</Button>

					{isEditMode && (
						<Button
							variant="subtle"
							color="red"
							onClick={handleRemove}
							fullWidth
							disabled={isLoading}
						>
							{tMipManage('root.loans.drawer.button.remove')}
						</Button>
					)}
				</Flex>
			</Flex>
		</StyledDrawer>
	);
};
